// src/pages/dashboard/hero-commands/CommandCard.tsx
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import CommandDialog from "./CommandDialog";
import { Loader2 } from "lucide-react";
import { toast } from "sonner"; // Assuming you use sonner for toasts
import { useToggleHeroCommandStatus, useSyncVapiAssistant, useAssistantId, useUpdateVapiPrompt } from "@/hooks/useHeroCommands";
import { ICommand, UserHeroCommand } from "@/types/heroCommands";

export default function CommandCard({ feature, userCommand }: { feature: ICommand, userCommand: UserHeroCommand | undefined }) {
  const toggleStatusMutation = useToggleHeroCommandStatus();
  const syncVapiMutation = useSyncVapiAssistant();
  const updateVapiPrompt = useUpdateVapiPrompt();
  
  const { data: assistantId, isLoading: settingsLoading } = useAssistantId();

  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleToggle = async () => {
    if (!userCommand) return toast.error("User command data is missing. Cannot toggle.")
    if (!assistantId) return toast.error("Assistant ID is missing. Cannot sync VAPI.")

    setError(null);
    try {
      // Step 1: Update the hero command's status in the database
      const newStatus = !userCommand.is_active;
      await toggleStatusMutation.mutateAsync({
        commandId: userCommand.id,
        newStatus
      });
      //step 2: update the vapi prompt
      await updateVapiPrompt.mutateAsync({assistantId});  

    } catch (err) {
      console.error('Error toggling command and updating VAPI:', err);
      setError(err instanceof Error ? err.message : 'Failed to update status. The command status in the database might be inconsistent with the AI configuration.');
    }
  };

  const isLoading = toggleStatusMutation.isPending || syncVapiMutation.isPending || settingsLoading || updateVapiPrompt.isPending;

  return (
    <div
      key={feature.title}
      className={`
        p-5 border rounded-lg flex flex-col justify-between bg-white
        ${!userCommand?.is_active ? 'bg-opacity-50' : 'hover:shadow-md transition-shadow border border-sky-600'}
      `}>
      <div className='mb-6'>
        <div className='flex items-center justify-between mb-2'>
          <h3 className={`text-3xl font-semibold text-gray-700 transition-opacity duration-300 ${!userCommand?.is_active && 'opacity-50'}`}>
            {feature.title}
          </h3>
          <div className="flex items-center space-x-2">
            {isLoading ? (
              <Loader2 className="size-4 animate-spin mx-2" />
            ) : (
              <Switch
                id={`toggle-${feature.id}`}
                checked={userCommand?.is_active || false}
                onCheckedChange={handleToggle}
                disabled={!userCommand  || isLoading || !assistantId}
                aria-label="Enable Feature"
              />
            )}
          </div>
        </div>
        <Badge variant="default" className="text-xs">{feature.category}</Badge>
        <p className={`text-sm mt-4 mb-4 text-gray-500`}>{feature.description}</p>
        {error && (
          <p className="text-sm text-red-500 mt-2">{error}</p>
        )}
      </div>
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogTrigger asChild>
          <Button
            variant='outline'
            disabled={isLoading}
            className="self-start"
          >
            {userCommand ? 'Customize & Deploy' : 'Configure & Deploy'}
          </Button>
        </AlertDialogTrigger>
        <CommandDialog
          assistantId={assistantId || ''}//just to be sure ve sent id to the dialog
          feature={feature}
          userCommand={userCommand}
          onOpenChange={setIsDialogOpen}
        />
      </AlertDialog>
    </div>
  );
}